import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import { solutionTeams, site } from '@/data/content';

const details: Record<
  string,
  { useCases: { icon: string; title: string; text: string }[]; outcomes: { value: string; label: string }[]; faqs: { q: string; a: string }[] }
> = {
  'customer-service': {
    useCases: [
      { icon: 'Inbox', title: 'Omnichannel tickets', text: 'Manage email, WhatsApp, live chat, and social messages in one shared inbox.' },
      { icon: 'Sparkles', title: 'AI reply suggestions', text: 'Zyra drafts replies and summarizes long threads for faster resolutions.' },
      { icon: 'BookOpen', title: 'Knowledge base deflection', text: 'Let customers find answers before they open a ticket.' },
      { icon: 'BarChart3', title: 'Performance dashboards', text: 'Track first response, CSAT, and resolution times in real time.' },
    ],
    outcomes: [
      { value: '2x', label: 'faster first response' },
      { value: '95%', label: 'CSAT score' },
      { value: '40%', label: 'less agent burnout' },
      { value: '30%', label: 'lower cost per ticket' },
    ],
    faqs: [
      { q: 'Can one agent handle all channels?', a: 'Yes. The unified inbox lets agents reply to WhatsApp, email, live chat, and social from one workspace.' },
      { q: 'How does the AI learn?', a: 'Zyra learns from your knowledge base, past replies, and team corrections to improve over time.' },
    ],
  },
  'marketing-and-sales': {
    useCases: [
      { icon: 'Target', title: 'Lead capture', text: 'Capture WhatsApp, Instagram, and live chat leads and auto-tag them by source.' },
      { icon: 'MessageCircle', title: 'Instant qualification', text: 'Ask qualification questions and score leads before handing them off.' },
      { icon: 'Users', title: 'Sales handoff', text: 'Route qualified leads to account executives with full context.' },
      { icon: 'BarChart3', title: 'Campaign tracking', text: 'See which campaigns drive the most conversations and conversions.' },
    ],
    outcomes: [
      { value: '35%', label: 'more leads captured' },
      { value: '40%', label: 'faster handoff' },
      { value: '25%', label: 'higher conversion' },
      { value: '2x', label: 'pipeline visibility' },
    ],
    faqs: [
      { q: 'Can we track which campaign a lead came from?', a: 'Yes. You can tag conversations by source, campaign, and UTM for full attribution.' },
      { q: 'Does ZuriHub integrate with CRM tools?', a: 'ZuriHub has an open API and webhooks. CRM integrations are available on Pro and Enterprise.' },
    ],
  },
};

export default function TeamSolution() {
  const { slug } = useParams<{ slug: string }>();
  const team = solutionTeams.find((t) => t.slug === slug);

  if (!team) {
    return (
      <section className="px-4 pt-32 pb-24 text-center">
        <h1 className="text-3xl font-extrabold text-slate-900">Solution not found</h1>
        <p className="mt-4 text-slate-600">Explore all ZuriHub solutions.</p>
        <Link to="/solutions" className="mt-6 inline-flex items-center gap-2 text-brand-600 font-bold">
          View solutions <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    );
  }

  const extra = details[team.slug] || { useCases: [], outcomes: [], faqs: [] };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: team.metaTitle,
      provider: { '@type': 'Organization', name: site.name, url: site.baseUrl },
      audience: { '@type': 'Audience', audienceType: team.name },
      url: `${site.baseUrl}/solutions/${team.slug}`,
      description: team.metaDescription,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: extra.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <>
      <SEO
        title={team.metaTitle}
        description={team.metaDescription}
        path={`/solutions/${team.slug}`}
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <Link to="/solutions" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-400 hover:text-white transition">
            <ArrowRight className="h-4 w-4 rotate-180" /> Solutions
          </Link>
          <div className="mt-6 grid items-center gap-10 lg:grid-cols-2">
            <div>
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-400 to-brand-700 shadow-lg">
                <Icon name={team.icon} className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                {team.headline}
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                {team.summary}
              </p>
              <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
                >
                  Start free trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/book-a-demo"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition hover:bg-slate-800"
                >
                  {team.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-900/50 p-8 backdrop-blur">
              <h2 className="text-xl font-bold text-white">{team.name} pain points</h2>
              <div className="mt-6 space-y-6">
                {team.painPoints.map((p) => (
                  <div key={p.title} className="flex gap-4">
                    <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-500/20 text-brand-400">
                      <Check className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{p.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{p.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="usecases-heading" className="section-title">How {team.name} teams use ZuriHub</h2>
            <p className="section-subtitle mx-auto">
              Workflows built around the goals of {team.name.toLowerCase()} teams.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {extra.useCases.map((uc) => (
              <div
                key={uc.title}
                className="rounded-3xl border border-slate-100 bg-slate-50 p-8 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner">
                  <Icon name={uc.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{uc.title}</h3>
                <p className="mt-3 text-slate-600">{uc.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white" aria-labelledby="outcomes-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="outcomes-heading" className="section-title text-white">Measurable outcomes</h2>
            <p className="section-subtitle mx-auto text-slate-300">
              Results {team.name.toLowerCase()} teams see with ZuriHub.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {extra.outcomes.map((o) => (
              <div
                key={o.label}
                className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8 text-center"
              >
                <p className="text-4xl font-extrabold text-brand-400">{o.value}</p>
                <p className="mt-2 text-lg font-semibold text-slate-200">{o.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why {team.name} teams choose ZuriHub</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {team.benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <Check className="h-4 w-4" />
                </div>
                <p className="text-lg font-semibold text-slate-900">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-4">
            {extra.faqs.map((f) => (
              <div
                key={f.q}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-6"
              >
                <h3 className="font-bold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-slate-900 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Power up your {team.name} team</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            Start a 14-day free trial and see how ZuriHub helps {team.name.toLowerCase()} teams move faster.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noreferrer"
            className="gradient-cta mt-8 inline-flex items-center gap-2"
          >
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
